import { findFocusableAncestor } from "@prismicio/editor-support/DOM"
import * as Tabs from "@radix-ui/react-tabs"
import type { ReactNode } from "react"

import type { SX } from "../../theme/new"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger
} from "../DropdownMenu"
import { IconButton } from "../IconButton"
import { Text } from "../Text"
import styles from "./Window.module.css"

interface WindowProps {
  children: ReactNode
  sx?: SX
}

export function Window(props: WindowProps) {
  const { sx, ...otherProps } = props
  return <div {...otherProps} style={sx} className={styles.window} />
}

export function WindowFrame() {
  return (
    <div className={styles.frame}>
      <div className={styles.titleBarOptions}>
        <span className={styles.titleBarOption} />
        <span className={styles.titleBarOption} />
        <span className={styles.titleBarOption} />
      </div>
    </div>
  )
}

interface WindowTabsProps {
  children: ReactNode
  defaultValue?: string
  onValueChange?: (value: string) => void
  value?: string
}

export function WindowTabs(props: WindowTabsProps) {
  return (
    <Tabs.Root {...props} activationMode="manual" className={styles.tabs} />
  )
}

interface WindowTabsListProps {
  children: ReactNode
  onAddNewTab?: () => void
}

export function WindowTabsList(props: WindowTabsListProps) {
  const { children, onAddNewTab, ...otherProps } = props
  return (
    <Tabs.List {...otherProps} className={styles.tabsList}>
      {children}
      <div className={styles.newTabButton}>
        <IconButton
          icon="add"
          onClick={onAddNewTab}
          hiddenLabel="Add new tab"
        />
      </div>
    </Tabs.List>
  )
}

interface WindowTabsTriggerProps {
  children: ReactNode
  value: string
  menu?: ReactNode
}

export function WindowTabsTrigger(props: WindowTabsTriggerProps) {
  const { children, menu, value, ...otherProps } = props
  return (
    <Tabs.Trigger
      {...otherProps}
      aria-label={value}
      asChild
      onMouseDown={(event) => {
        const target = event.target as HTMLElement
        const focusableAncestor = findFocusableAncestor(target)
        if (
          focusableAncestor !== event.currentTarget &&
          focusableAncestor !== event.currentTarget.parentElement
        ) {
          event.preventDefault()
        }
        if (target === event.currentTarget) {
          target.scrollIntoView({
            behavior: "smooth",
            block: "nearest",
            inline: "center"
          })
        }
      }}
      value={value}
    >
      <div className={styles.tabsTrigger}>
        <Text
          className={styles.tabsTriggerText}
          color="inherit"
          component="span"
          noWrap
          variant="emphasized"
        >
          {children}
        </Text>
        {menu ? (
          <div className={styles.tabsTriggerMenu}>
            <DropdownMenu>
              <DropdownMenuTrigger>
                <IconButton
                  icon="moreVert"
                  hiddenLabel={`tab-${value}-menu-button`}
                />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">{menu}</DropdownMenuContent>
            </DropdownMenu>
          </div>
        ) : undefined}
      </div>
    </Tabs.Trigger>
  )
}

interface WindowTabsContentProps {
  children: ReactNode
  value: string
}

export function WindowTabsContent(props: WindowTabsContentProps) {
  return <Tabs.Content {...props} className={styles.tabsContent} />
}
